<template>
  <div class="aberration-schematic-viz">
    
    <div class="schematic-header">
       <div class="title">透镜像差分析图 (Optic Aberration)</div>
       <div class="status">系统状态: {{ mode === 'spherical' ? '球差分析' : '色散分析' }}</div>
    </div>

    <div class="simulation-view">
      <div class="svg-container">
        <svg width="100%" height="300" viewBox="0 0 500 300" preserveAspectRatio="xMidYMid meet">
          <defs>
            <filter id="glow">
              <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            <linearGradient id="spectrum" x1="0%" y1="0%" x2="0%" y2="100%">
               <stop offset="0%" stop-color="#ff4757" />
               <stop offset="50%" stop-color="#2ed573" />
               <stop offset="100%" stop-color="#4da1ff" />
            </linearGradient>
          </defs>

          <!-- 光轴 -->
          <line x1="20" y1="150" x2="480" y2="150" stroke="#334155" stroke-dasharray="8,4" />

          <!-- 透镜示意 -->
          <path d="M 200,60 Q 240,150 200,240 Q 160,150 200,60" fill="rgba(148, 163, 184, 0.15)" stroke="#64748b" stroke-width="2" />
          <text x="185" y="45" fill="#94a3b8" font-size="10" font-weight="bold">凸透镜 (Lens)</text>

          <!-- 平行光输入 -->
          <g opacity="0.3">
             <line v-for="i in 7" :key="'in'+i" x1="20" :y1="105+i*11" x2="185" :y2="105+i*11" :stroke="mode === 'chromatic' ? 'url(#spectrum)' : '#cbd5e1'" stroke-width="1.5" />
          </g>

          <!-- 汇聚光线 -->
          <g filter="url(#glow)">
            <!-- 球差演示 (外周汇聚早) -->
            <template v-if="mode === 'spherical'">
               <line x1="215" y1="105" x2="380" y2="150" stroke="#fbbf24" stroke-width="1.5" /> <!-- outer bottom -->
               <line x1="215" y1="195" x2="380" y2="150" stroke="#fbbf24" stroke-width="1.5" /> <!-- outer top -->
               <line x1="215" y1="150" x2="430" y2="150" stroke="#fbbf24" stroke-width="1.5" /> <!-- center -->
               <circle cx="380" cy="150" r="3" fill="#fbbf24" />
               <text x="370" y="140" fill="#fbbf24" font-size="9">外周焦点</text>
               <circle cx="430" cy="150" r="3" fill="#fbbf24" opacity="0.6" />
               <text x="420" y="140" fill="#94a3b8" font-size="9">中心焦点</text>
            </template>

            <!-- 色差演示 (蓝光折射大) -->
            <template v-else>
               <line x1="215" y1="150" x2="360" y2="150" stroke="#60a5fa" stroke-width="2" /> <!-- Blue focus -->
               <line x1="215" y1="150" x2="440" y2="150" stroke="#f87171" stroke-width="2" /> <!-- Red focus -->
               <circle cx="360" cy="150" r="3" fill="#60a5fa" />
               <text x="350" y="165" fill="#60a5fa" font-size="9">蓝光 (短波)</text>
               <circle cx="440" cy="150" r="3" fill="#f87171" />
               <text x="430" y="165" fill="#f87171" font-size="9">红光 (长波)</text>
            </template>
          </g>
        </svg>
      </div>

      <div class="schematic-controls">
         <div class="mode-selector">
            <el-radio-group v-model="mode" size="mini">
               <el-radio-button label="spherical">球差 (Spherical)</el-radio-button>
               <el-radio-button label="chromatic">色差 (Chromatic)</el-radio-button>
            </el-radio-group>
         </div>

         <div class="explanation-box">
            <p v-if="mode === 'spherical'"><strong>球差现象：</strong>由于球面透镜边缘处的汇聚能力比中心区域强，导致远轴光线折射后会比近轴光线更早交汇。这种成像缺陷会导致图像边缘模糊。</p>
            <p v-else><strong>色差现象：</strong>介质对不同波长的光折射率不同（色散）。蓝光频率高折射强，焦点更近；红光折射弱，焦点较远。这是高质镜头需要消色差组的原因。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'LensAberrationViz',
  data() {
    return {
      mode: 'spherical'
    }
  }
}
</script>

<style scoped lang="scss">
.aberration-schematic-viz {
  flex: 1; display: flex; flex-direction: column; background: #0f172a; border-radius: 12px; overflow: hidden;
  border: 1px solid #1e293b; color: #f8fafc;
}

.schematic-header {
  padding: 15px 20px; background: #1e293b; border-bottom: 2px solid #334155;
  display: flex; justify-content: space-between; align-items: center;
  .title { font-size: 0.9rem; font-weight: 800; color: #38bdf8; }
  .status { font-size: 0.75rem; color: #94a3b8; font-family: monospace; }
}

.simulation-view { flex: 1; display: flex; flex-direction: column; }

.svg-container { flex: 1.5; background: #020617; position: relative; border-bottom: 1px solid #1e293b; }

.schematic-controls {
  flex: 1; padding: 20px; display: flex; flex-direction: column; gap: 15px;
  .mode-selector { display: flex; justify-content: center; }
  .explanation-box {
     padding: 15px; border-radius: 8px; background: rgba(56, 189, 248, 0.05); border: 1px dashed #334155;
     p { margin: 0; font-size: 0.8rem; line-height: 1.6; color: #cbd5e1; }
     strong { color: #38bdf8; }
  }
}

::v-deep .el-radio-button__inner { background: #1e293b; color: #94a3b8; border-color: #334155; }
::v-deep .el-radio-button__orig-radio:checked + .el-radio-button__inner { background: #38bdf8; border-color: #38bdf8; color: #0f172a; box-shadow: none; }
</style>
